import { IonButton, IonIcon, IonProgressBar } from '@ionic/react';
import {
  checkmarkCircleOutline,
  chevronForwardOutline,
  folderOpenOutline,
  timeOutline,
} from 'ionicons/icons';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  resetToIdle,
  selectActiveImports,
  selectCurrentPhase,
  selectEstimatedTimeRemaining,
  selectIsImporting,
  selectOverallProgress,
  selectScanningProgress,
} from '../../store/slices/global-import-progress.slice';
import { AppDispatch } from '../../store/store';
import './GlobalProgressBar.scss';

interface GlobalProgressBarProps {
  className?: string;
}

const GlobalProgressBar: React.FC<GlobalProgressBarProps> = ({ className }) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const isImporting = useSelector(selectIsImporting);
  const overallProgress = useSelector(selectOverallProgress);
  const activeImports = useSelector(selectActiveImports);
  const currentPhase = useSelector(selectCurrentPhase);
  const estimatedTimeRemaining = useSelector(selectEstimatedTimeRemaining);
  const scanningProgress = useSelector(selectScanningProgress);

  // Auto-hide when complete
  useEffect(() => {
    if (currentPhase === 'complete') {
      const timer = setTimeout(() => {
        dispatch(resetToIdle());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentPhase, dispatch]);

  // Don't render if not importing
  if (!isImporting && currentPhase !== 'complete') {
    return null;
  }

  const handleClick = () => {
    // Navigate to music sources page
    history.push('/my-library/sources');
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getPhaseText = (): string => {
    switch (currentPhase) {
      case 'scanning':
        if (scanningProgress) {
          const {
            directoriesScanned,
            totalDirectories,
            filesFound,
            currentDirectory,
          } = scanningProgress;

          if (totalDirectories > 0) {
            const dirProgress = Math.round(
              (directoriesScanned / totalDirectories) * 100,
            );
            if (currentDirectory) {
              const dirName =
                currentDirectory.split('/').pop() || currentDirectory;
              return `Scanning directories (${dirProgress}%) - ${dirName}`;
            }
            return `Scanning directories (${dirProgress}%) - ${directoriesScanned}/${totalDirectories} directories`;
          }

          if (filesFound > 0) {
            return `Scanning directories - ${filesFound.toLocaleString()} files found`;
          }

          return 'Scanning directories...';
        }
        return 'Scanning directories...';
      case 'processing':
        return 'Processing files...';
      case 'complete':
        return 'Import complete!';
      default:
        return 'Importing...';
    }
  };

  const getProgressColor = (): string => {
    if (currentPhase === 'complete') return 'success';
    if (activeImports.some((imp) => imp.errors.length > 0)) return 'warning';
    return 'primary';
  };

  const totalErrors = activeImports.reduce(
    (sum, imp) => sum + imp.errors.length,
    0,
  );
  const totalFiles = activeImports.reduce(
    (sum, imp) => sum + imp.totalFiles,
    0,
  );
  const processedFiles = activeImports.reduce(
    (sum, imp) => sum + imp.processedFiles,
    0,
  );

  return (
    <div className={`global-progress-bar ${className || ''}`}>
      <IonButton
        fill="clear"
        size="small"
        className="progress-button"
        onClick={handleClick}
      >
        <div className="progress-content">
          <IonProgressBar
            value={overallProgress / 100}
            color={getProgressColor()}
            className="progress-bar-main"
          />

          <div className="progress-info">
            <div className="progress-text">
              <span className="progress-phase">{getPhaseText()}</span>
              <div className="progress-stats">
                <span className="progress-percentage">{overallProgress}%</span>
                {currentPhase === 'scanning' && scanningProgress && (
                  <>
                    {scanningProgress.totalDirectories > 0 && (
                      <span className="directory-count">
                        <IonIcon icon={folderOpenOutline} />
                        {scanningProgress.directoriesScanned.toLocaleString()}/
                        {scanningProgress.totalDirectories.toLocaleString()}{' '}
                        dirs
                      </span>
                    )}
                    {scanningProgress.filesFound > 0 && (
                      <span className="files-found">
                        <IonIcon icon={folderOpenOutline} />
                        {scanningProgress.filesFound.toLocaleString()} files
                        found
                      </span>
                    )}
                  </>
                )}
                {currentPhase === 'processing' && totalFiles > 0 && (
                  <span className="file-count">
                    <IonIcon icon={folderOpenOutline} />
                    {processedFiles.toLocaleString()}/
                    {totalFiles.toLocaleString()}
                  </span>
                )}
                {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
                  <span className="time-remaining">
                    <IonIcon icon={timeOutline} />
                    {formatTime(estimatedTimeRemaining)}
                  </span>
                )}
                {totalErrors > 0 && (
                  <span className="error-count">
                    {totalErrors} error{totalErrors > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <IonIcon
              icon={
                currentPhase === 'complete'
                  ? checkmarkCircleOutline
                  : chevronForwardOutline
              }
              className="nav-icon"
            />
          </div>
        </div>
      </IonButton>
    </div>
  );
};

export default GlobalProgressBar;
